class DttsFacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this._pendingForm = null;
    this._renderVersion = 0;
    this._debouncedApply = this._debounce(() => {
      if (this._pendingForm) this.applyFilters(this._pendingForm);
    }, 500);
  }

  connectedCallback() {
    this.addEventListener('click', this._onDelegatedClick.bind(this));
    this.addEventListener('change', this._onChange.bind(this));
    this.addEventListener('submit', this._onSubmit.bind(this));
    this.addEventListener('keydown', this._onKeyDown.bind(this));

    if (!DttsFacetFiltersForm._globalBound) {
      DttsFacetFiltersForm._globalBound = true;
      window.addEventListener('popstate', () => {
        document.querySelectorAll('dtts-facet-filters-form').forEach((el) => el.renderFromUrl());
      });
      document.addEventListener('click', (event) => {
        document.querySelectorAll('dtts-facet-filters-form form[data-dtts-form="desktop"] details[open]').forEach((d) => {
          if (!d.contains(event.target)) d.removeAttribute('open');
        });
      });
    }
  }

  _onDelegatedClick(event) {
    const target = event.target;

    if (target.closest('[data-dtts-filter-open]')) {
      this._openDrawer();
      return;
    }

    if (target.closest('[data-dtts-filter-close]')) {
      this._closeDrawer();
      return;
    }

    if (target.closest('[data-dtts-filter-apply]')) {
      const mobileForm = this.querySelector('form[data-dtts-form="mobile"]');
      if (mobileForm) this.applyFilters(mobileForm);
      this._closeDrawer();
      return;
    }

    const clearLink = target.closest('.dtts-filter-drawer__clear');
    if (clearLink) {
      event.preventDefault();
      const href = clearLink.getAttribute('href');
      if (href && href.startsWith('/')) {
        history.pushState({ dttsFacets: true }, '', href);
        this.renderFromUrl();
        this._closeDrawer();
      }
    }
  }

  _onChange(event) {
    const form = event.target.closest('form');
    if (!form) return;
    if (event.target.type === 'number') return;
    this._pendingForm = form;
    this._debouncedApply();
  }

  _onSubmit(event) {
    event.preventDefault();
    const form = event.target.closest('form');
    if (!form) return;
    this.applyFilters(form);
    this._closeDrawer();
  }

  _onKeyDown(event) {
    if (event.key !== 'Enter') return;
    if (event.target.type !== 'number') return;
    event.preventDefault();
    const form = event.target.closest('form');
    if (form) this.applyFilters(form);
  }

  _openDrawer() {
    const drawer = this.querySelector('[data-dtts-filter-drawer]');
    if (!drawer) return;
    drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  _closeDrawer() {
    const drawer = this.querySelector('[data-dtts-filter-drawer]');
    if (!drawer) return;
    drawer.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  _debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  _getSearchParams(form) {
    if (!form) return '';
    const data = new FormData(form);
    const params = new URLSearchParams();
    for (const [key, value] of data.entries()) {
      if (value === '' || value == null) continue;
      params.append(key, value);
    }
    return params.toString();
  }

  applyFilters(form) {
    const search = this._getSearchParams(form);
    const pathname = window.location.pathname;
    const newUrl = `${pathname}${search ? '?' + search : ''}`;
    if (newUrl !== window.location.pathname + window.location.search) {
      history.pushState({ dttsFacets: true }, '', newUrl);
    }
    this.renderFromUrl();
  }

  async renderFromUrl() {
    const filterSectionId = this.dataset.sectionId;
    const gridEl = document.querySelector('[data-dtts-product-grid-section]');
    if (!filterSectionId || !gridEl) return;
    const gridSectionId = gridEl.dataset.dttsProductGridSection;
    if (!gridSectionId) return;

    const version = ++this._renderVersion;
    const pathname = window.location.pathname;
    const search = window.location.search.slice(1);
    const buildUrl = (sid) => `${pathname}?section_id=${encodeURIComponent(sid)}${search ? '&' + search : ''}`;

    this._setLoading(true);
    try {
      const [filterHtml, gridHtml] = await Promise.all([
        fetch(buildUrl(filterSectionId)).then((r) => r.text()),
        fetch(buildUrl(gridSectionId)).then((r) => r.text()),
      ]);
      if (version !== this._renderVersion) return;
      this._swapSection(filterHtml, '[data-dtts-product-filter-section]', this);
      this._swapDrawer(filterHtml);
      this._swapSection(gridHtml, '[data-dtts-product-grid-section]', document);
    } catch (err) {
      console.error('[DTTS facets] render failed', err);
    } finally {
      if (version === this._renderVersion) this._setLoading(false);
    }
  }

  _swapSection(html, selector, scope) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const source = doc.querySelector(selector);
    const target = scope.querySelector(selector);
    if (!source || !target) return;
    const openIndices = this._getOpenDetails(target);
    target.innerHTML = source.innerHTML;
    this._restoreOpenDetails(target, openIndices);
  }

  _swapDrawer(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const sourceDrawer = doc.querySelector('[data-dtts-filter-drawer]');
    const targetDrawer = this.querySelector('[data-dtts-filter-drawer]');
    if (!sourceDrawer || !targetDrawer) return;
    const wasOpen = targetDrawer.classList.contains('is-open');
    const openIndices = this._getOpenDetails(targetDrawer);
    targetDrawer.innerHTML = sourceDrawer.innerHTML;
    if (wasOpen) targetDrawer.classList.add('is-open');
    this._restoreOpenDetails(targetDrawer, openIndices);
  }

  _getOpenDetails(container) {
    const indices = [];
    container.querySelectorAll('details').forEach((d, i) => {
      if (d.open) indices.push(i);
    });
    return indices;
  }

  _restoreOpenDetails(container, indices) {
    const details = container.querySelectorAll('details');
    indices.forEach((i) => {
      if (details[i]) details[i].open = true;
    });
  }

  _setLoading(loading) {
    const grid = document.querySelector('[data-dtts-product-grid-section]');
    if (grid) grid.classList.toggle('dtts-facets-loading', loading);
  }
}

if (!customElements.get('dtts-facet-filters-form')) {
  customElements.define('dtts-facet-filters-form', DttsFacetFiltersForm);
}
